import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Heart } from "lucide-react";
import { BUSINESS } from "@/data/site";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/portal");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand-deep flex flex-col items-center justify-center px-5 py-12">
      <Link to="/" data-testid="login-back-home" className="flex items-center gap-2 text-sm text-charcoal-soft hover:text-charcoal mb-8 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to website
      </Link>

      <div className="w-full max-w-md bg-white rounded-[2rem] soft-shadow-lg border border-charcoal/5 p-8 sm:p-10">
        <div className="flex flex-col items-center text-center mb-8">
          <img src={BUSINESS.logo} alt="HomeCare by Nicole" className="h-20 w-auto object-contain mb-3" />
          <h1 className="font-serif text-3xl text-charcoal">Care Portal</h1>
          <p className="text-sm text-charcoal-soft mt-1">Sign in to manage your care</p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="text-charcoal-soft">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="mt-2 h-12 rounded-xl border-charcoal/10"
              required
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-charcoal-soft">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2 h-12 rounded-xl border-charcoal/10"
              required
            />
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sage text-white py-4 font-medium hover:bg-sage-dark transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Heart className="h-4 w-4" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>

      <p className="text-xs text-charcoal-soft mt-6">
        HomeCare by Nicole · {BUSINESS.area}
      </p>
    </div>
  );
}
